/**
 * Geography utilities — flags, labels, and filter options for all countries.
 *
 * Uses `country-flag-icons` for SVG flags and `i18n-iso-countries` for
 * country names. No hardcoded country list to maintain.
 */

import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import * as flags from 'country-flag-icons/string/3x2';

countries.registerLocale(enLocale);

const toDataUrl = (svg: string) =>
  `data:image/svg+xml,${encodeURIComponent(svg)}`;

// Geographies not in ISO 3166-1
const SPECIAL_LABELS: Record<string, string> = {
  eu: 'European Union',
  uk: 'United Kingdom',
  us: 'United States',
  world: 'International (Non-Jurisdictional)',
  unknown: 'Unknown',
};

const SPECIAL_FLAGS: Record<string, string> = {
  eu: toDataUrl((flags as Record<string, string>).EU),
  uk: toDataUrl((flags as Record<string, string>).GB),
};

/** Flag data-URL for any geography code (lowercase). */
export function getGeographyFlag(code: string): string | undefined {
  if (SPECIAL_FLAGS[code]) return SPECIAL_FLAGS[code];
  const svg = (flags as Record<string, string>)[code.toUpperCase()];
  return svg ? toDataUrl(svg) : undefined;
}

/** Human-readable label for any geography code (lowercase). */
export function getGeographyLabel(code: string): string {
  if (SPECIAL_LABELS[code]) return SPECIAL_LABELS[code];
  return countries.getName(code.toUpperCase(), 'en') ?? code;
}

/**
 * Comparator for regulatory region lists: `world` (International Non-Jurisdictional)
 * always sorts last; all other codes sort A–Z by display label.
 */
export function compareGeographyCodesForFilter(a: string, b: string): number {
  const aWorld = a.toLowerCase() === 'world';
  const bWorld = b.toLowerCase() === 'world';
  if (aWorld !== bWorld) return aWorld ? 1 : -1;
  return getGeographyLabel(a).localeCompare(getGeographyLabel(b));
}

export type GeographyOption = {
  label: string;
  value: string;
  icon?: string;
};

// Pre-computed list of every country + EU + world
const ALL_OPTIONS: GeographyOption[] = (() => {
  const isoCodes = Object.keys(countries.getAlpha2Codes()).map(c =>
    c.toLowerCase()
  );
  const allCodes = [...new Set(['eu', 'world', ...isoCodes])];
  return allCodes
    .filter(c => c !== 'unknown')
    .map(code => ({
      label: getGeographyLabel(code),
      value: code,
      icon: getGeographyFlag(code),
    }))
    .sort((a, b) => compareGeographyCodesForFilter(a.value, b.value));
})();

/**
 * Geography options for filter dropdowns.
 *
 * - No argument → all countries (admin / back-office mode)
 * - With codes  → only those geographies (scoped mode, e.g. regulatory watch)
 */
export function getGeographyOptions(codes?: string[]): GeographyOption[] {
  if (!codes) return ALL_OPTIONS;
  return codes
    .filter(c => c !== 'unknown')
    .map(code => ({
      label: getGeographyLabel(code),
      value: code,
      icon: getGeographyFlag(code),
    }))
    .sort((a, b) => compareGeographyCodesForFilter(a.value, b.value));
}
